import { useNavHistory } from '@portals/shared-frontend/hooks';
import type { NavNodeType, ParentInfo } from '@portals/shared-frontend/utils';
import { useEffect } from 'react';

/**
 * Hook to track the current route
 *
 * Call this in your page/route components with the actual data.
 * You can optionally specify parent nodes to control the hierarchy.
 *
 * Note: name and type are required - if data might not be loaded yet,
 * use the TrackRoute component with conditional rendering instead:
 * {data && <TrackRoute path="/path" name={data.name} type="team" />}
 *
 * @example
 * // Simple page with no parents - static data
 * useTrackRoute('/scope-overview', 'Scope Overview', 'generic');
 *
 * @example
 * // Application nested under team - all data available
 * useTrackRoute(
 *   `/team/${teamId}/application/${appId}`,
 *   'API Management',
 *   'app',
 *   [{ path: `/team/${teamId}`, name: 'Open Banking team', type: 'team' }]
 * );
 *
 * @example
 * // API nested under application and team - all data available
 * useTrackRoute(
 *   `/team/${teamId}/application/${appId}/api/${apiId}`,
 *   'Checkout API',
 *   'api',
 *   [
 *     { path: `/team/${teamId}`, name: 'Team Name', type: 'team' },
 *     { path: `/team/${teamId}/application/${appId}`, name: 'API Management', type: 'app' }
 *   ]
 * );
 */
export function useTrackRoute(path: string, name: string, type: NavNodeType, parents?: ParentInfo[]) {
  const { trackPage } = useNavHistory();

  useEffect(() => {
    trackPage(path, name, type, parents);
  }, [path, name, type, parents, trackPage]);
}

/**
 * Component version for tracking routes
 *
 * Place this in your page component to automatically track visits.
 * Optionally specify parents array to control nesting hierarchy.
 *
 * Note: name and type are required - use conditional rendering if data might not be loaded yet.
 *
 * @example
 * // Static page - name is always known
 * <TrackRoute path="/scope-overview" name="Scope Overview" type="generic" />
 *
 * @example
 * // Team page with no parent
 * const { data } = useSWR('/api/team/123');
 * {data && <TrackRoute path="/team/123" name={data.teamName} type="team" />}
 *
 * @example
 * // Application nested under team
 * {app && teamId && (
 *   <TrackRoute
 *     path={`/team/${teamId}/application/${appId}`}
 *     name={app.name}
 *     type="app"
 *     parents={[{ path: `/team/${teamId}`, type: 'team' }]}
 *   />
 * )}
 *
 * @example
 * // API nested under application and team
 * {api && teamId && (
 *   <TrackRoute
 *     path={`/team/${teamId}/application/${appId}/api/${apiId}`}
 *     name={api.name}
 *     type="api"
 *     parents={[
 *       { path: `/team/${teamId}`, type: 'team' },
 *       { path: `/team/${teamId}/application/${appId}`, type: 'app' }
 *     ]}
 *   />
 * )}
 */
export function TrackRoute({
  path,
  name,
  type,
  parents,
}: {
  path: string;
  name: string;
  type: NavNodeType;
  parents?: ParentInfo[];
}) {
  useTrackRoute(path, name, type, parents);
  return null;
}
